import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import { invalidateMonthlyBatchQueries, MONTHLY_BATCHES_QUERY_KEY } from '@/lib/monthly-batch-query';
import {
  acceptDispatchRequest,
  cancelSupplierDispatchRequest,
  createSupplierDispatchRequest,
  deleteDispatchChain,
  fetchDeletedDispatches,
  fetchDispatchRequests,
  fetchDispatchLocationPresets,
  fetchFacilities,
  fetchFacilityDispatchRequests,
  fetchMonthlyBatchOptions,
  fetchPlatformDispatchRequests,
  fetchRecentDispatchVehicles,
  previewDispatchDelete,
  fetchSupplierDispatchRequests,
  fulfillDispatchRequest,
  rejectFacilityDispatchRequest,
  rejectPlatformDispatchRequest,
  returnDispatchRequestToDraft,
  restoreDeletedDispatch,
  routeDispatchRequest,
  saveDispatchLocationPreset,
  submitSupplierDispatchRequest,
  updateSupplierDispatchRequest,
  updateProcessorDispatchRequest,
  type DispatchListParams,
  type DispatchListResult,
  type DeletedDispatchListResult,
  type DispatchFacilityParams,
  type DispatchLocationPresetParams,
} from './api';
import type {
  DispatchLocationPreset,
  DispatchDeletePreview,
  DispatchDeleteResult,
  DispatchLocationPresetCreateInput,
  DispatchMonthlyBatchOption,
  DispatchRecentVehicle,
  DispatchRequestAcceptInput,
  DispatchRequestCancelInput,
  DispatchRequestCreateInput,
  DispatchRequestDto,
  DispatchRequestFulfillInput,
  DispatchRequestRejectInput,
  DispatchRequestReturnToDraftInput,
  DispatchRequestRouteInput,
  DispatchRequestUpdateInput,
  FacilityDto,
} from './types';

const KEY = 'dispatch-requests' as const;
const FACILITIES_KEY = 'facilities' as const;
const BATCHES_KEY = MONTHLY_BATCHES_QUERY_KEY;
const LOCATION_PRESETS_KEY = 'dispatch-location-presets' as const;

const invalidateDispatchAndMonthlyQueries = (qc: QueryClient) => {
  void qc.invalidateQueries({ queryKey: [KEY] });
  invalidateMonthlyBatchQueries(qc);
};

export const invalidateDispatchChainQueries = (qc: QueryClient) => {
  void qc.invalidateQueries({ queryKey: [KEY] });
  void qc.invalidateQueries({ queryKey: ['inbound-lots'] });
  void qc.invalidateQueries({ queryKey: ['sorting-packages'] });
  void qc.invalidateQueries({ queryKey: ['sorting-decisions'] });
  void qc.invalidateQueries({ queryKey: ['dismantling-packages'] });
  void qc.invalidateQueries({ queryKey: ['dismantling-sessions'] });
  void qc.invalidateQueries({ queryKey: ['inventory'] });
  void qc.invalidateQueries({ queryKey: ['weight-difference-checks'] });
  void qc.invalidateQueries({ queryKey: ['evidence'] });
  void qc.invalidateQueries({ queryKey: ['file-library'] });
  void qc.invalidateQueries({ queryKey: ['supplier-portal'] });
  void qc.invalidateQueries({ queryKey: ['mass-balance'] });
  void qc.invalidateQueries({ queryKey: ['first-valuations'] });
  void qc.invalidateQueries({ queryKey: ['exceptions', 'target-picker'] });
  void qc.invalidateQueries({ queryKey: ['correction-requests', 'targets'] });
  void qc.invalidateQueries({ queryKey: ['audit-package', 'precheck'] });
  void qc.invalidateQueries({ queryKey: ['secondary-process-packages'] });
  void qc.invalidateQueries({ queryKey: ['disassembly-batches', 'inbound-packages'] });
  void qc.invalidateQueries({ queryKey: ['monthly-material-valuation'] });
  void qc.invalidateQueries({ queryKey: ['getit-purchase-price'] });
  invalidateMonthlyBatchQueries(qc);
  void qc.invalidateQueries({ queryKey: ['monthly-reports'] });
  void qc.invalidateQueries({ queryKey: ['dashboard'] });
};

// ---------------------------------------------------------------------------
// Supplier hooks
// ---------------------------------------------------------------------------

export const useSupplierRequests = (params: DispatchListParams = {}) => {
  return useQuery<DispatchListResult, ApiClientError>({
    queryKey: [KEY, 'supplier', 'list', params],
    queryFn: () => fetchSupplierDispatchRequests(params),
    placeholderData: (prev) => prev,
  });
};

export const useDispatchRequests = (params: DispatchListParams = {}, enabled = true) => {
  return useQuery<DispatchListResult, ApiClientError>({
    queryKey: [KEY, 'unified', 'list', params],
    queryFn: () => fetchDispatchRequests(params),
    enabled,
    placeholderData: (prev) => prev,
  });
};

export const useCreateSupplierRequest = () => {
  const qc = useQueryClient();
  return useMutation<DispatchRequestDto, ApiClientError, DispatchRequestCreateInput>({
    mutationFn: createSupplierDispatchRequest,
    onSuccess: () => invalidateDispatchAndMonthlyQueries(qc),
  });
};

export const useUpdateSupplierRequest = () => {
  const qc = useQueryClient();
  return useMutation<
    DispatchRequestDto,
    ApiClientError,
    { id: string; body: DispatchRequestUpdateInput; processor?: boolean }
  >({
    mutationFn: ({ id, body, processor }) =>
      processor
        ? updateProcessorDispatchRequest(id, body)
        : updateSupplierDispatchRequest(id, body),
    onSuccess: () => invalidateDispatchAndMonthlyQueries(qc),
  });
};

export const useSubmitSupplierRequest = () => {
  const qc = useQueryClient();
  return useMutation<
    DispatchRequestDto,
    ApiClientError,
    string | { id: string; ccEmails?: string[] }
  >({
    mutationFn: (value) =>
      typeof value === 'string'
        ? submitSupplierDispatchRequest(value)
        : submitSupplierDispatchRequest(value.id, value.ccEmails),
    onSuccess: () => invalidateDispatchAndMonthlyQueries(qc),
  });
};

export const useCancelSupplierRequest = () => {
  const qc = useQueryClient();
  return useMutation<
    DispatchRequestDto,
    ApiClientError,
    { id: string; body: DispatchRequestCancelInput }
  >({
    mutationFn: ({ id, body }) => cancelSupplierDispatchRequest(id, body),
    onSuccess: () => invalidateDispatchAndMonthlyQueries(qc),
  });
};

export const useDispatchLocationPresets = (
  params: DispatchLocationPresetParams & { enabled?: boolean } = {},
) => {
  const { enabled = true, ...queryParams } = params;
  return useQuery<DispatchLocationPreset[], ApiClientError>({
    queryKey: [LOCATION_PRESETS_KEY, 'list', queryParams],
    queryFn: () => fetchDispatchLocationPresets(queryParams),
    enabled,
    placeholderData: (prev) => prev,
  });
};

export const useSaveDispatchLocationPreset = () => {
  const qc = useQueryClient();
  return useMutation<DispatchLocationPreset, ApiClientError, DispatchLocationPresetCreateInput>({
    mutationFn: saveDispatchLocationPreset,
    onSuccess: () => qc.invalidateQueries({ queryKey: [LOCATION_PRESETS_KEY] }),
  });
};

// ---------------------------------------------------------------------------
// Platform hooks
// ---------------------------------------------------------------------------

export const usePlatformRequests = (params: DispatchListParams = {}) => {
  return useQuery<DispatchListResult, ApiClientError>({
    queryKey: [KEY, 'platform', 'list', params],
    queryFn: () => fetchPlatformDispatchRequests(params),
    placeholderData: (prev) => prev,
  });
};

export const useRouteRequest = () => {
  const qc = useQueryClient();
  return useMutation<
    DispatchRequestDto,
    ApiClientError,
    { id: string; body: DispatchRequestRouteInput }
  >({
    mutationFn: ({ id, body }) => routeDispatchRequest(id, body),
    onSuccess: () => invalidateDispatchAndMonthlyQueries(qc),
  });
};

export const useRejectPlatformRequest = () => {
  const qc = useQueryClient();
  return useMutation<
    DispatchRequestDto,
    ApiClientError,
    { id: string; body: DispatchRequestRejectInput }
  >({
    mutationFn: ({ id, body }) => rejectPlatformDispatchRequest(id, body),
    onSuccess: () => invalidateDispatchAndMonthlyQueries(qc),
  });
};

export const useReturnDispatchRequestToDraft = () => {
  const qc = useQueryClient();
  return useMutation<
    DispatchRequestDto,
    ApiClientError,
    { id: string; body: DispatchRequestReturnToDraftInput }
  >({
    mutationFn: ({ id, body }) => returnDispatchRequestToDraft(id, body),
    onSuccess: () => invalidateDispatchAndMonthlyQueries(qc),
  });
};

export const useDispatchDeletePreview = (id: string | undefined, enabled: boolean) => {
  return useQuery<DispatchDeletePreview, ApiClientError>({
    queryKey: [KEY, 'delete-preview', id],
    queryFn: () => previewDispatchDelete(id!),
    enabled: enabled && !!id,
  });
};

export const useDeleteDispatchChain = () => {
  const qc = useQueryClient();
  return useMutation<DispatchDeleteResult, ApiClientError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => deleteDispatchChain(id, { reason }),
    onSuccess: () => invalidateDispatchChainQueries(qc),
  });
};

export const useDeletedDispatches = (
  params: Pick<DispatchListParams, 'page' | 'per_page'> = {},
  enabled = true,
) => {
  return useQuery<DeletedDispatchListResult, ApiClientError>({
    queryKey: [KEY, 'deleted', params],
    queryFn: () => fetchDeletedDispatches(params),
    enabled,
    placeholderData: (prev) => prev,
  });
};

export const useRestoreDeletedDispatch = () => {
  const qc = useQueryClient();
  return useMutation<DispatchDeleteResult, ApiClientError, string>({
    mutationFn: restoreDeletedDispatch,
    onSuccess: () => invalidateDispatchChainQueries(qc),
  });
};

// ---------------------------------------------------------------------------
// Facility hooks
// ---------------------------------------------------------------------------

export const useFacilityRequests = (
  facilityId: string | undefined,
  params: DispatchListParams = {},
) => {
  return useQuery<DispatchListResult, ApiClientError>({
    queryKey: [KEY, 'facility', 'list', facilityId, params],
    queryFn: () => fetchFacilityDispatchRequests(facilityId!, params),
    enabled: !!facilityId,
    placeholderData: (prev) => prev,
  });
};

export const useAcceptRequest = () => {
  const qc = useQueryClient();
  return useMutation<
    DispatchRequestDto,
    ApiClientError,
    { id: string; body: DispatchRequestAcceptInput }
  >({
    mutationFn: ({ id, body }) => acceptDispatchRequest(id, body),
    onSuccess: () => invalidateDispatchAndMonthlyQueries(qc),
  });
};

/**
 * Plates this facility used on its recent acceptances — offered as one-tap
 * suggestions so the operator does not retype the same truck every time.
 */
export const useRecentDispatchVehicles = (
  facilityId: string | null | undefined,
  options: { enabled?: boolean; limit?: number } = {},
) => {
  const { enabled = true, limit = 5 } = options;
  return useQuery<DispatchRecentVehicle[], ApiClientError>({
    queryKey: [KEY, 'recent-vehicles', facilityId, limit],
    queryFn: () => fetchRecentDispatchVehicles({ facility_id: facilityId!, limit }),
    enabled: enabled && !!facilityId,
    placeholderData: (prev) => prev,
  });
};

export const useRejectFacilityRequest = () => {
  const qc = useQueryClient();
  return useMutation<
    DispatchRequestDto,
    ApiClientError,
    { id: string; body: DispatchRequestRejectInput }
  >({
    mutationFn: ({ id, body }) => rejectFacilityDispatchRequest(id, body),
    onSuccess: () => invalidateDispatchAndMonthlyQueries(qc),
  });
};

export const useFulfillRequest = () => {
  const qc = useQueryClient();
  return useMutation<
    DispatchRequestDto,
    ApiClientError,
    { id: string; body: DispatchRequestFulfillInput }
  >({
    mutationFn: ({ id, body }) => fulfillDispatchRequest(id, body),
    onSuccess: () => invalidateDispatchAndMonthlyQueries(qc),
  });
};

// ---------------------------------------------------------------------------
// Dropdown helpers
// ---------------------------------------------------------------------------

export const useFacilities = (params: DispatchFacilityParams = {}) => {
  return useQuery<FacilityDto[], ApiClientError>({
    queryKey: [FACILITIES_KEY, 'list', params],
    queryFn: () => fetchFacilities(params),
    placeholderData: (prev) => prev,
  });
};

export const useMonthlyBatchesList = () => {
  return useQuery<DispatchMonthlyBatchOption[], ApiClientError>({
    queryKey: [BATCHES_KEY, 'list'],
    queryFn: fetchMonthlyBatchOptions,
    placeholderData: (prev) => prev,
  });
};
